import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeftRight, ArrowLeft, Save, Send, Check, X, Loader2, AlertTriangle, Info, Ban, Eye } from 'lucide-react';
import { useDateFormat } from '../../hooks/useDateFormat';
import SearchableAccountPicker from './SearchableAccountPicker';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const REASONS = ['Transmission', 'Secondary', 'Other'];
const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-700 border-gray-300',
  pending_approval: 'bg-amber-100 text-amber-800 border-amber-300',
  approved: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  rejected: 'bg-red-100 text-red-800 border-red-300',
  committed: 'bg-blue-100 text-blue-800 border-blue-300',
  reversed: 'bg-orange-100 text-orange-800 border-orange-300',
  cancelled: 'bg-gray-200 text-gray-700 border-gray-300',
};

const fmt = (n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

export default function CommitmentTransferForm() {
  const fmtDate = useDateFormat();
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [record, setRecord] = useState(null); // existing record for edit / view

  // Form state
  const [transferorAccountId, setTransferorAccountId] = useState('');
  const [fundId, setFundId] = useState('');
  const [className, setClassName] = useState('');
  const [reason, setReason] = useState('Secondary');
  const [reasonNote, setReasonNote] = useState('');
  const [transferType, setTransferType] = useState('partial');
  const [agreementDate, setAgreementDate] = useState('');
  const [transfereeAccountId, setTransfereeAccountId] = useState('');
  const [transfereePpmNo, setTransfereePpmNo] = useState('');
  const [transfereeFolioMode, setTransfereeFolioMode] = useState('new');

  const [commitmentAmt, setCommitmentAmt] = useState('');
  const [selectedTxns, setSelectedTxns] = useState({}); // {id: {picked, amount, units, date}}
  const [outstandingOverrides, setOutstandingOverrides] = useState({}); // {di_id: amount}

  // ------ Tranche-level customize (FIFO override) ------
  // When null → FIFO (default). When populated → user has customised per-
  // tranche amounts and we send them as `tranche_allocations` on submit.
  const [tranches, setTranches] = useState([]);                // ledger for this class
  const [customizeTranches, setCustomizeTranches] = useState(false);
  const [trancheOverrides, setTrancheOverrides] = useState({}); // {tranche_id: amount}
  const [tranchesLoading, setTranchesLoading] = useState(false);

  // Lookups
  const [allAccounts, setAllAccounts] = useState([]);
  const [transferorFunds, setTransferorFunds] = useState([]);
  const [transferees, setTransferees] = useState([]);
  const [elig, setElig] = useState(null);
  const [eligLoading, setEligLoading] = useState(false);
  const [transfereeHoldsInFund, setTransfereeHoldsInFund] = useState(false);

  // Load all accounts once (dropdown source)
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/investor-accounts`, authHeader());
        setAllAccounts(Array.isArray(data) ? data : data.accounts || []);
      } catch { /* ignore */ }
    })();
  }, []);

  // Fetch existing record (edit / view mode)
  useEffect(() => {
    if (!id) return;
    setLoading(true);
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/transfers/${id}`, authHeader());
        setRecord(data);
        setTransferorAccountId(data.transferor_account_id);
        setFundId(data.fund_id);
        setClassName(data.class_name);
        setReason(data.reason);
        setReasonNote(data.reason_note || '');
        setTransferType(data.transfer_type);
        setAgreementDate(data.agreement_date);
        setTransfereeAccountId(data.transferee_account_id);
        setTransfereePpmNo(data.transferee_ppm_no || '');
        setTransfereeFolioMode(data.transferee_folio_mode || 'new');
        setCommitmentAmt(data.commitment_amount_transferred);
        const sel = {};
        (data.selected_transactions || []).forEach(s => {
          sel[s.source_transaction_id] = {
            picked: true, amount: s.transferred_amount, units: s.transferred_units,
            date: s.transferred_date || s.original_date || '',
          };
        });
        setSelectedTxns(sel);
        const ov = {};
        (data.outstanding_drawdown_transferred || []).forEach(od => {
          ov[od.drawdown_investor_id] = od.outstanding_amount;
        });
        setOutstandingOverrides(ov);
      } catch (e) {
        setError(e.response?.data?.detail || 'Failed to load transfer.');
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  // When transferor changes → load funds
  useEffect(() => {
    if (!transferorAccountId) return;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API_URL}/transfers/transferor-funds?transferor_account_id=${transferorAccountId}`,
          authHeader(),
        );
        setTransferorFunds(data.funds || []);
        // Reset dependent fields if not editing
        if (!isEdit) { setFundId(''); setClassName(''); setElig(null); }
      } catch { setTransferorFunds([]); }
    })();
  }, [transferorAccountId, isEdit]);

  // When fund changes → load transferees + auto-pick class if only one
  useEffect(() => {
    if (!fundId) return;
    const fund = transferorFunds.find(f => f.fund_id === fundId);
    if (fund && fund.classes && fund.classes.length === 1 && !className) {
      setClassName(fund.classes[0]);
    }
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/transfers/transferees?fund_id=${fundId}`, authHeader());
        setTransferees(data.accounts || []);
      } catch { setTransferees([]); }
    })();
  }, [fundId, transferorFunds, className]);

  // When (transferor, fund, class) tuple is set → load eligibility
  useEffect(() => {
    if (!transferorAccountId || !fundId || !className) return;
    setEligLoading(true);
    (async () => {
      try {
        const { data } = await axios.post(`${API_URL}/transfers/eligibility`, {
          transferor_account_id: transferorAccountId, fund_id: fundId, class_name: className,
        }, authHeader());
        setElig(data);
      } catch (e) {
        setElig(null);
        setError(e.response?.data?.detail || 'Failed to load transferor position.');
      } finally { setEligLoading(false); }
    })();
  }, [transferorAccountId, fundId, className]);

  // When transferee changes → check if they hold in this fund
  useEffect(() => {
    if (!transfereeAccountId) { setTransfereeHoldsInFund(false); return; }
    const t = transferees.find(x => x.id === transfereeAccountId);
    setTransfereeHoldsInFund(!!(t && t.holds_in_fund));
    if (t && !t.holds_in_fund) setTransfereeFolioMode('new');
  }, [transfereeAccountId, transferees]);

  const currentFund = useMemo(() => transferorFunds.find(f => f.fund_id === fundId) || null, [transferorFunds, fundId]);
  const fundClasses = currentFund?.classes || [];

  // Recompute summary totals from selected transactions
  const summaryFromTxns = useMemo(() => {
    let amt = 0, units = 0;
    Object.entries(selectedTxns).forEach(([, v]) => {
      if (v?.picked) { amt += Number(v.amount || 0); units += Number(v.units || 0); }
    });
    return { amt: Math.round(amt * 100) / 100, units: Math.round(units * 10000) / 10000 };
  }, [selectedTxns]);

  // Seed / refresh proportional defaults for outstanding drawdowns whenever
  // the commitment amount or eligibility snapshot changes. User overrides are
  // preserved — this only fills in blank rows.
  useEffect(() => {
    if (!elig || !elig.outstanding_drawdowns?.length) return;
    const held = Number(elig.held_commitment || 0);
    const xfer = Number(commitmentAmt || 0);
    const fraction = held > 0 ? Math.min(xfer / held, 1) : 0;
    setOutstandingOverrides(prev => {
      const next = { ...prev };
      elig.outstanding_drawdowns.forEach(od => {
        // Only fill when the user hasn't explicitly touched this row yet.
        if (next[od.drawdown_investor_id] === undefined || next[od.drawdown_investor_id] === '') {
          next[od.drawdown_investor_id] = Math.round(od.outstanding_amount * fraction * 100) / 100;
        }
      });
      return next;
    });
  }, [elig, commitmentAmt]);

  // Fetch tranche ledger for the picked (transferor, fund, class) so the
  // "Customize" toggle can show and let the user override FIFO.
  useEffect(() => {
    setTranches([]);
    setTrancheOverrides({});
    setCustomizeTranches(false);
    if (!transferorAccountId || !fundId || !className) return;
    (async () => {
      setTranchesLoading(true);
      try {
        const { data } = await axios.get(
          `${API_URL}/transfers/tranches?account_id=${encodeURIComponent(transferorAccountId)}`
          + `&fund_id=${encodeURIComponent(fundId)}`
          + `&class_name=${encodeURIComponent(className)}`,
          authHeader(),
        );
        setTranches(data?.tranches || []);
      } catch {
        setTranches([]);
      } finally {
        setTranchesLoading(false);
      }
    })();
    // eslint-disable-next-line
  }, [transferorAccountId, fundId, className]);

  // FIFO auto-preview — computes {tranche_id: consumed_amount} for the
  // current commitmentAmt. Used when customizeTranches=false OR to seed
  // the picker when customizeTranches is turned ON.
  const fifoPreview = useMemo(() => {
    const xfer = Number(commitmentAmt || 0);
    if (!xfer || tranches.length === 0) return {};
    let remaining = xfer;
    const out = {};
    for (const t of tranches) {
      if (remaining <= 0.005) break;
      const take = Math.min(remaining, Number(t.amount || 0));
      if (take > 0) out[t.tranche_id] = Math.round(take * 100) / 100;
      remaining -= take;
    }
    return out;
  }, [commitmentAmt, tranches]);

  // Seed picker with FIFO amounts whenever the user turns on Customize.
  useEffect(() => {
    if (customizeTranches) setTrancheOverrides(fifoPreview);
    // eslint-disable-next-line
  }, [customizeTranches]);

  const trancheAllocationTotal = useMemo(
    () => Object.values(trancheOverrides).reduce(
      (s, v) => s + Number(v || 0), 0,
    ),
    [trancheOverrides],
  );
  const trancheAllocationOK = customizeTranches
    ? Math.abs(trancheAllocationTotal - Number(commitmentAmt || 0)) < 0.01
    : true;

  const isReadOnly = record && record.status !== 'draft';

  const toggleTxn = (t) => {
    if (isReadOnly) return;
    setSelectedTxns(prev => {
      const cur = prev[t.source_transaction_id];
      if (cur?.picked) return { ...prev, [t.source_transaction_id]: { ...cur, picked: false } };
      return {
        ...prev,
        [t.source_transaction_id]: {
          picked: true, amount: t.original_amount, units: t.original_units, date: t.original_date,
        },
      };
    });
  };

  const buildPayload = () => ({
    reason, reason_note: reasonNote, transfer_type: transferType,
    fund_id: fundId, class_name: className,
    transferor_account_id: transferorAccountId,
    transferee_account_id: transfereeAccountId,
    transferee_ppm_no: transfereePpmNo,
    transferee_folio_mode: transfereeFolioMode,
    agreement_date: agreementDate,
    commitment_amount_transferred: transferType === 'full'
      ? Number(elig?.held_commitment ?? commitmentAmt ?? 0)
      : Number(commitmentAmt || 0),
    // Contribution + units are server-derived from selected_transactions —
    // send 0 as a placeholder; the API will overwrite them with the sum.
    contribution_amount_transferred: 0,
    units_transferred: 0,
    selected_transactions: Object.entries(selectedTxns)
      .filter(([, v]) => v?.picked)
      .map(([txnId, v]) => {
        const src = (elig?.transactions || []).find(t => t.source_transaction_id === txnId) || {};
        return {
          source_transaction_id: txnId,
          original_amount: src.original_amount || 0,
          original_units: src.original_units || 0,
          original_date: src.original_date || null,
          transferred_amount: Number(v.amount || 0),
          transferred_units: Number(v.units || 0),
          transferred_date: v.date || src.original_date || null,
          edited: (Number(v.amount) !== src.original_amount) || (Number(v.units) !== src.original_units),
        };
      }),
    outstanding_drawdown_overrides: Object.entries(outstandingOverrides).map(([di_id, amt]) => ({
      drawdown_investor_id: di_id, outstanding_amount: Number(amt || 0),
    })),
    // Include per-tranche allocations only when the user has actively
    // customised them. Missing → server falls back to FIFO by
    // agreement_date ascending (matches the ledger order the user sees).
    tranche_allocations: customizeTranches
      ? tranches
          .filter((t) => Number(trancheOverrides[t.tranche_id] || 0) > 0)
          .map((t) => ({
            tranche_id: t.tranche_id,
            amount: Number(trancheOverrides[t.tranche_id] || 0),
          }))
      : [],
  });

  const doSaveDraft = async () => {
    setSaving(true); setError('');
    try {
      const payload = buildPayload();
      const call = record
        ? axios.put(`${API_URL}/transfers/${record.id}`, payload, authHeader())
        : axios.post(`${API_URL}/transfers`, payload, authHeader());
      const { data } = await call;
      setRecord(data);
      if (!record) navigate(`/investors/transfers/${data.id}`, { replace: true });
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to save transfer.');
    } finally { setSaving(false); }
  };

  const doSubmit = async () => {
    if (!record) return;
    setSaving(true); setError('');
    try {
      const { data } = await axios.post(`${API_URL}/transfers/${record.id}/submit`, {}, authHeader());
      setRecord(data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to submit.');
    } finally { setSaving(false); }
  };

  const doApprove = async () => {
    if (!record) return;
    const note = window.prompt('Approval note (optional):') || '';
    setSaving(true); setError('');
    try {
      const { data } = await axios.post(`${API_URL}/transfers/${record.id}/approve`, { note }, authHeader());
      setRecord(data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to approve.');
    } finally { setSaving(false); }
  };

  const doReject = async () => {
    if (!record) return;
    const note = window.prompt('Reason for rejection (required):');
    if (!note || !note.trim()) return;
    setSaving(true); setError('');
    try {
      const { data } = await axios.post(`${API_URL}/transfers/${record.id}/reject`, { note }, authHeader());
      setRecord(data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to reject.');
    } finally { setSaving(false); }
  };

  const doCommit = async () => {
    if (!record) return;
    if (!window.confirm(
      'COMMIT this transfer?\n\n'
      + 'This will mutate account balances, transactions, and reassign outstanding drawdowns.\n'
      + 'The action can be reversed later using "Reverse Transfer".',
    )) return;
    const note = window.prompt('Commit note (optional):') || '';
    setSaving(true); setError('');
    try {
      const { data } = await axios.post(`${API_URL}/transfers/${record.id}/commit`, { note }, authHeader());
      setRecord(data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to commit.');
    } finally { setSaving(false); }
  };

  const doReverse = async () => {
    if (!record) return;
    const note = window.prompt('Reason for reversal (required):');
    if (!note || !note.trim()) return;
    if (!window.confirm(
      'REVERSE this committed transfer?\n\n'
      + 'Both accounts will be restored to their exact pre-commit state.',
    )) return;
    setSaving(true); setError('');
    try {
      const { data } = await axios.post(`${API_URL}/transfers/${record.id}/reverse`, { note }, authHeader());
      setRecord(data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to reverse.');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center min-h-screen"><Loader2 className="animate-spin" /></div>;

  const preview = record?.preview_snapshot;

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="commitment-transfer-form">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/investors/transfers')} className="text-gray-500 hover:text-gray-900" data-testid="transfer-back">
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-2xl font-bold text-gray-900 inline-flex items-center gap-2">
            <ArrowLeftRight className="text-blue-600" size={22} />
            {record ? `Transfer ${record.transfer_reference}` : 'New Commitment Transfer'}
          </h1>
          {record && (
            <span className={`px-2 py-0.5 text-xs rounded-full border ${STATUS_COLORS[record.status] || ''}`}>
              {record.status.replace('_', ' ')}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {record?.status === 'draft' && (
            <>
              <button onClick={doSaveDraft} disabled={saving} className="px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50 inline-flex items-center gap-1.5" data-testid="transfer-save-draft">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save Draft
              </button>
              <button onClick={doSubmit} disabled={saving} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 inline-flex items-center gap-1.5" data-testid="transfer-submit">
                <Send size={14} /> Submit for Approval
              </button>
            </>
          )}
          {!record && (
            <button onClick={doSaveDraft} disabled={saving} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 inline-flex items-center gap-1.5" data-testid="transfer-save-new">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save Draft
            </button>
          )}
          {record?.status === 'pending_approval' && (
            <>
              <button onClick={doApprove} disabled={saving} className="px-3 py-1.5 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 inline-flex items-center gap-1.5" data-testid="transfer-approve">
                <Check size={14} /> Approve
              </button>
              <button onClick={doReject} disabled={saving} className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 inline-flex items-center gap-1.5" data-testid="transfer-reject">
                <X size={14} /> Reject
              </button>
            </>
          )}
          {record?.status === 'approved' && (
            <button onClick={doCommit} disabled={saving} className="px-3 py-1.5 text-sm bg-blue-700 text-white rounded-lg hover:bg-blue-800 inline-flex items-center gap-1.5" data-testid="transfer-commit">
              <Check size={14} /> Commit Transfer (Mutate Balances)
            </button>
          )}
          {record?.status === 'committed' && (
            <button onClick={doReverse} disabled={saving} className="px-3 py-1.5 text-sm bg-orange-600 text-white rounded-lg hover:bg-orange-700 inline-flex items-center gap-1.5" data-testid="transfer-reverse">
              <ArrowLeftRight size={14} /> Reverse Transfer
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2 text-sm text-red-800" data-testid="transfer-error">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <div>{error}</div>
        </div>
      )}

      <div className={`border rounded-lg p-3 flex items-start gap-2 text-xs ${record?.status === 'committed' ? 'bg-blue-50 border-blue-200 text-blue-900' : record?.status === 'reversed' ? 'bg-orange-50 border-orange-200 text-orange-900' : 'bg-blue-50 border-blue-100 text-blue-900'}`}>
        <Info size={14} className="mt-0.5 shrink-0" />
        <div>
          {record?.status === 'committed' ? (
            <><strong>Committed — balances mutated.</strong> Transferor commitment reduced, transferee credited, mirror transactions written, and outstanding drawdowns reassigned. Use <em>Reverse Transfer</em> to restore both accounts to the exact pre-commit state.</>
          ) : record?.status === 'reversed' ? (
            <><strong>Reversed.</strong> Both accounts have been restored to their pre-commit state. This transfer is now closed.</>
          ) : (
            <><strong>Phase A — no balance mutation yet.</strong> This screen validates conservation
            (transferred + retained = original for commitment / contribution / units), captures maker-checker
            approval, and builds a read-only preview. Actual money & unit movement happens on <em>Commit Transfer</em>.</>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Transferor panel */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 space-y-3" data-testid="transferor-panel">
          <h2 className="font-semibold text-gray-800 border-b pb-2">Transferor</h2>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Investor Account *</label>
            <SearchableAccountPicker
              value={transferorAccountId}
              onChange={setTransferorAccountId}
              accounts={allAccounts}
              disabled={isReadOnly}
              placeholder="— Search transferor by name or PAN —"
              testId="transferor-account"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Fund *</label>
            <select value={fundId} onChange={e => setFundId(e.target.value)} disabled={isReadOnly || !transferorAccountId} className="w-full text-sm border rounded-lg px-3 py-2 bg-white disabled:bg-gray-50" data-testid="transferor-fund">
              <option value="">— Select fund —</option>
              {transferorFunds.map(f => (<option key={f.fund_id} value={f.fund_id}>{f.fund_name}</option>))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Class of Units {fundClasses.length > 1 ? '*' : ''}</label>
            {fundClasses.length <= 1 ? (
              <input value={className} disabled className="w-full text-sm border rounded-lg px-3 py-2 bg-gray-50 text-gray-700" data-testid="transferor-class" placeholder="—" />
            ) : (
              <select value={className} onChange={e => setClassName(e.target.value)} disabled={isReadOnly} className="w-full text-sm border rounded-lg px-3 py-2 bg-white disabled:bg-gray-50" data-testid="transferor-class">
                <option value="">— Select class —</option>
                {fundClasses.map(c => (<option key={c} value={c}>{c}</option>))}
              </select>
            )}
          </div>

          {eligLoading && <div className="text-xs text-gray-500 flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> Loading position…</div>}
          {elig && (
            <div className="rounded-lg border border-blue-100 bg-blue-50/50 px-3 py-2 text-xs text-blue-900 space-y-1" data-testid="transferor-position">
              <div><span className="font-medium">Folio:</span> {elig.transferor_folio_no || '—'}</div>
              <div><span className="font-medium">Held Commitment:</span> {fmt(elig.held_commitment)}</div>
              <div><span className="font-medium">Held Contribution:</span> {fmt(elig.held_contribution)}</div>
              <div><span className="font-medium">Held Units:</span> {fmt(elig.held_units)}</div>
              {elig.outstanding_drawdowns.length > 0 && (
                <div className="text-amber-900"><span className="font-medium">Outstanding drawdowns:</span> {elig.outstanding_drawdowns.length} row(s), total {fmt(elig.outstanding_drawdowns.reduce((a, x) => a + x.outstanding_amount, 0))}</div>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Commitment to Transfer {transferType !== 'full' && '*'}
                {transferType === 'full' && (
                  <span className="ml-1 text-[10px] text-gray-500 font-normal">
                    (auto-set to held commitment for Full transfer)
                  </span>
                )}
              </label>
              <input
                type="number"
                value={transferType === 'full' ? (elig?.held_commitment ?? '') : commitmentAmt}
                onChange={e => setCommitmentAmt(e.target.value)}
                disabled={isReadOnly || transferType === 'full'}
                className="w-full text-sm border rounded-lg px-3 py-2 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                data-testid="transfer-commitment-amount"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Transfer Type *</label>
              <select
                value={transferType}
                onChange={e => {
                  const nt = e.target.value;
                  setTransferType(nt);
                  // Auto-sync commitmentAmt to held on switching to Full so
                  // the submitted payload is correct even when the field is
                  // rendered as disabled/hidden.
                  if (nt === 'full' && elig?.held_commitment != null) {
                    setCommitmentAmt(String(elig.held_commitment));
                  }
                }}
                disabled={isReadOnly}
                className="w-full text-sm border rounded-lg px-3 py-2 bg-white disabled:bg-gray-50"
                data-testid="transfer-type"
              >
                <option value="partial">Partial</option>
                <option value="full">Full</option>
              </select>
            </div>
          </div>

          {/* Tranche picker (FIFO by default, per-tranche override on toggle).
              Shown as soon as tranches load so the user can see the ledger
              before entering an amount — FIFO Draw column stays "—" until
              commitmentAmt > 0. */}
          {tranches.length > 0 && (
            <div className="border rounded-lg bg-white overflow-hidden">
              <div className="px-3 py-2 bg-blue-50 border-b flex items-center justify-between">
                <div className="text-xs font-semibold text-blue-900">
                  Commitment Ledger — {className} · {tranches.length} tranche{tranches.length === 1 ? '' : 's'}
                </div>
                <label className={`text-xs font-semibold text-blue-900 flex items-center gap-1.5 ${(isReadOnly || Number(commitmentAmt || 0) <= 0) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`} data-testid="customize-tranches-toggle">
                  <input
                    type="checkbox"
                    checked={customizeTranches}
                    onChange={(e) => setCustomizeTranches(e.target.checked)}
                    disabled={isReadOnly || Number(commitmentAmt || 0) <= 0}
                    className="rounded"
                  />
                  Customize per-tranche
                </label>
              </div>
              <table className="w-full text-xs">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">Agreement Date</th>
                    <th className="px-3 py-2 text-right font-semibold">Tranche Balance</th>
                    <th className="px-3 py-2 text-right font-semibold">
                      {customizeTranches ? 'Draw from Tranche' : 'FIFO Draw'}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {tranches.map((t) => {
                    const previewAmt = fifoPreview[t.tranche_id] || 0;
                    const overrideAmt = trancheOverrides[t.tranche_id];
                    return (
                      <tr key={t.tranche_id} className="border-t" data-testid={`tranche-row-${t.tranche_id}`}>
                        <td className="px-3 py-2 text-gray-800">
                          {t.agreement_date || <span className="italic text-gray-400">no date</span>}
                        </td>
                        <td className="px-3 py-2 text-right font-mono">{Number(t.amount).toLocaleString()}</td>
                        <td className="px-3 py-2 text-right">
                          {customizeTranches ? (
                            <input
                              type="number" min="0" step="0.01"
                              value={overrideAmt ?? ''}
                              onChange={(e) => setTrancheOverrides((prev) => ({
                                ...prev, [t.tranche_id]: e.target.value,
                              }))}
                              disabled={isReadOnly}
                              className="w-32 text-right border rounded px-2 py-1 font-mono text-xs"
                              data-testid={`tranche-input-${t.tranche_id}`}
                            />
                          ) : (
                            <span className={previewAmt > 0 ? 'font-mono text-blue-700 font-semibold' : 'text-gray-300 font-mono'}>
                              {previewAmt > 0 ? previewAmt.toLocaleString() : '—'}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-gray-50 border-t">
                  <tr>
                    <td className="px-3 py-2 text-right font-semibold text-gray-700" colSpan={2}>Total drawn</td>
                    <td className="px-3 py-2 text-right font-mono font-bold" data-testid="tranche-total">
                      {customizeTranches
                        ? Number(trancheAllocationTotal || 0).toLocaleString()
                        : Object.values(fifoPreview).reduce((s, v) => s + v, 0).toLocaleString()}
                      <span className="text-gray-500 font-normal ml-2">/ {Number(commitmentAmt || 0).toLocaleString()}</span>
                    </td>
                  </tr>
                </tfoot>
              </table>
              {customizeTranches && !trancheAllocationOK && (
                <div className="px-3 py-2 bg-red-50 border-t border-red-200 text-xs text-red-700 font-medium" data-testid="tranche-error">
                  Allocations must total exactly {Number(commitmentAmt || 0).toLocaleString()}
                  {' '}(currently {trancheAllocationTotal.toLocaleString()}). Adjust the amounts above.
                </div>
              )}
              {!customizeTranches && (
                <div className="px-3 py-2 bg-blue-50 border-t border-blue-100 text-[11px] text-blue-800">
                  <span className="font-semibold">FIFO default</span> — consumes oldest tranche first.
                  {Number(commitmentAmt || 0) > 0
                    ? ' Toggle "Customize per-tranche" above to override.'
                    : ' Enter a Commitment to Transfer amount above to preview the draw.'}
                </div>
              )}
            </div>
          )}
          {tranchesLoading && (
            <div className="text-xs text-gray-500 italic">Loading tranche ledger…</div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Contribution Transferred (auto)</label>
              <div className="w-full text-sm border rounded-lg px-3 py-2 bg-blue-50 text-blue-900 font-semibold" data-testid="transfer-contribution-amount">
                {fmt(summaryFromTxns.amt)}
              </div>
              <p className="text-[10px] text-gray-500 mt-0.5">= Σ transferred amounts on selected transactions</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Units Transferred (auto)</label>
              <div className="w-full text-sm border rounded-lg px-3 py-2 bg-blue-50 text-blue-900 font-semibold" data-testid="transfer-units">
                {fmt(summaryFromTxns.units)}
              </div>
              <p className="text-[10px] text-gray-500 mt-0.5">= Σ transferred units on selected transactions</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Reason *</label>
              <select value={reason} onChange={e => setReason(e.target.value)} disabled={isReadOnly} className="w-full text-sm border rounded-lg px-3 py-2 bg-white disabled:bg-gray-50" data-testid="transfer-reason">
                {REASONS.map(r => (<option key={r} value={r}>{r}</option>))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Agreement Date *</label>
              <input type="date" value={agreementDate} onChange={e => setAgreementDate(e.target.value)} disabled={isReadOnly} className="w-full text-sm border rounded-lg px-3 py-2 disabled:bg-gray-50" data-testid="transfer-agreement-date" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Reason Note</label>
            <textarea rows={2} value={reasonNote} onChange={e => setReasonNote(e.target.value)} disabled={isReadOnly} className="w-full text-sm border rounded-lg px-3 py-2 disabled:bg-gray-50" data-testid="transfer-reason-note" />
          </div>

          {elig?.transactions?.length > 0 && (() => {
            const allPicked = elig.transactions.every(t => selectedTxns[t.source_transaction_id]?.picked);
            const somePicked = elig.transactions.some(t => selectedTxns[t.source_transaction_id]?.picked);
            const toggleAll = () => {
              if (isReadOnly) return;
              if (allPicked) {
                // clear every picked row
                setSelectedTxns(prev => {
                  const next = { ...prev };
                  elig.transactions.forEach(t => {
                    if (next[t.source_transaction_id]) {
                      next[t.source_transaction_id] = { ...next[t.source_transaction_id], picked: false };
                    }
                  });
                  return next;
                });
              } else {
                // pick every row, preserving any user-edited amount/units/date
                setSelectedTxns(prev => {
                  const next = { ...prev };
                  elig.transactions.forEach(t => {
                    const cur = next[t.source_transaction_id];
                    next[t.source_transaction_id] = cur
                      ? { ...cur, picked: true }
                      : { picked: true, amount: t.original_amount, units: t.original_units, date: t.original_date };
                  });
                  return next;
                });
              }
            };
            return (
            <div className="border rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-700 border-b flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={allPicked}
                  ref={el => { if (el) el.indeterminate = somePicked && !allPicked; }}
                  onChange={toggleAll}
                  disabled={isReadOnly}
                  data-testid="txn-pick-all"
                  aria-label="Select all transactions to carry over"
                  title={allPicked ? 'Clear all' : 'Select all'}
                />
                <span>Select transactions to carry over</span>
                <span className="ml-auto text-[10px] font-normal text-gray-500">
                  {elig.transactions.filter(t => selectedTxns[t.source_transaction_id]?.picked).length} / {elig.transactions.length} selected
                </span>
              </div>
              {(() => {
                // Group transactions by drawdown_number so users can pick
                // every txn that paid a specific drawdown with one click.
                const byDd = new Map();
                elig.transactions.forEach(t => {
                  if (!t.drawdown_number) return;
                  if (!byDd.has(t.drawdown_number)) byDd.set(t.drawdown_number, []);
                  byDd.get(t.drawdown_number).push(t);
                });
                if (byDd.size === 0) return null;
                const toggleByDd = (ddNumber, rows) => {
                  if (isReadOnly) return;
                  const allChipPicked = rows.every(r => selectedTxns[r.source_transaction_id]?.picked);
                  setSelectedTxns(prev => {
                    const next = { ...prev };
                    rows.forEach(r => {
                      const cur = next[r.source_transaction_id];
                      if (allChipPicked) {
                        if (cur) next[r.source_transaction_id] = { ...cur, picked: false };
                      } else {
                        next[r.source_transaction_id] = cur
                          ? { ...cur, picked: true }
                          : { picked: true, amount: r.original_amount, units: r.original_units, date: r.original_date };
                      }
                    });
                    return next;
                  });
                };
                return (
                  <div className="bg-blue-50/40 px-3 py-2 border-b flex flex-wrap items-center gap-1.5">
                    <span className="text-[10px] uppercase tracking-wider text-blue-800 font-semibold mr-1">Select by drawdown</span>
                    {[...byDd.entries()].map(([ddNumber, rows]) => {
                      const allChipPicked = rows.every(r => selectedTxns[r.source_transaction_id]?.picked);
                      const someChipPicked = rows.some(r => selectedTxns[r.source_transaction_id]?.picked);
                      return (
                        <button
                          key={ddNumber}
                          type="button"
                          onClick={() => toggleByDd(ddNumber, rows)}
                          disabled={isReadOnly}
                          className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] rounded-full border font-medium transition
                            ${allChipPicked
                              ? 'bg-blue-600 text-white border-blue-600'
                              : someChipPicked
                                ? 'bg-blue-100 text-blue-800 border-blue-300'
                                : 'bg-white text-blue-700 border-blue-200 hover:bg-blue-100'}
                            disabled:opacity-50 disabled:cursor-not-allowed`}
                          title={allChipPicked
                            ? `Clear all ${rows.length} transaction(s) for ${ddNumber}`
                            : `Pick all ${rows.length} transaction(s) that paid ${ddNumber}`}
                          data-testid={`txn-pick-dd-${ddNumber}`}
                        >
                          ↳ {ddNumber}
                          <span className="opacity-70">· {rows.length}</span>
                        </button>
                      );
                    })}
                  </div>
                );
              })()}
              <div className="max-h-60 overflow-y-auto">
                {elig.transactions.map(t => {
                  const sel = selectedTxns[t.source_transaction_id];
                  const picked = !!sel?.picked;
                  return (
                    <div key={t.source_transaction_id} className={`px-3 py-2 text-xs border-b last:border-b-0 flex items-center gap-2 ${picked ? 'bg-blue-50/50' : ''}`}>
                      <input type="checkbox" checked={picked} onChange={() => toggleTxn(t)} disabled={isReadOnly} data-testid={`txn-pick-${t.source_transaction_id}`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-gray-800 truncate flex items-center gap-1.5">
                          <span>{fmtDate(t.original_date)} · {t.transaction_type}</span>
                          {t.drawdown_number && (
                            <span
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium bg-blue-100 text-blue-700 rounded-full"
                              title="This contribution paid this drawdown — it will be carried over along with a proportional share of the drawdown."
                              data-testid={`txn-dd-${t.source_transaction_id}`}
                            >
                              ↳ {t.drawdown_number}
                            </span>
                          )}
                        </div>
                        <div className="text-gray-500">₹{fmt(t.original_amount)} · {fmt(t.original_units)} units</div>
                      </div>
                      {picked && (
                        <div className="flex items-center gap-1">
                          <input type="number" value={sel.amount} onChange={e => setSelectedTxns(p => ({ ...p, [t.source_transaction_id]: { ...sel, amount: e.target.value } }))} disabled={isReadOnly} className="w-24 border rounded px-1 py-0.5 text-xs" data-testid={`txn-amt-${t.source_transaction_id}`} />
                          <input type="number" step="0.0001" value={sel.units} onChange={e => setSelectedTxns(p => ({ ...p, [t.source_transaction_id]: { ...sel, units: e.target.value } }))} disabled={isReadOnly} className="w-20 border rounded px-1 py-0.5 text-xs" data-testid={`txn-units-${t.source_transaction_id}`} />
                          <input type="date" value={sel.date} onChange={e => setSelectedTxns(p => ({ ...p, [t.source_transaction_id]: { ...sel, date: e.target.value } }))} disabled={isReadOnly} className="w-32 border rounded px-1 py-0.5 text-xs" data-testid={`txn-date-${t.source_transaction_id}`} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            );
          })()}

          {elig?.outstanding_drawdowns?.length > 0 && (
            <div className="border rounded-lg overflow-hidden" data-testid="outstanding-drawdowns-block">
              <div className="bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-900 border-b flex justify-between items-center">
                <span>Outstanding drawdowns to reassign</span>
                <span className="text-[10px] font-normal text-amber-800">
                  Default = proportional to commitment ({Number(commitmentAmt || 0) > 0 && Number(elig.held_commitment) > 0
                    ? Math.round((Number(commitmentAmt) / Number(elig.held_commitment)) * 100)
                    : 0}%). Editable per row.
                </span>
              </div>
              <div className="max-h-48 overflow-y-auto text-xs">
                {elig.outstanding_drawdowns.map(od => (
                  <div key={od.drawdown_investor_id} className="px-3 py-2 border-b last:border-b-0 flex items-center gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-gray-800 truncate">{od.drawdown_number || od.drawdown_id}</div>
                      <div className="text-gray-500">Full outstanding: ₹{fmt(od.outstanding_amount)}</div>
                    </div>
                    <input
                      type="number"
                      value={outstandingOverrides[od.drawdown_investor_id] ?? ''}
                      max={od.outstanding_amount}
                      min={0}
                      onChange={e => setOutstandingOverrides(p => ({ ...p, [od.drawdown_investor_id]: e.target.value }))}
                      disabled={isReadOnly}
                      className="w-28 border rounded px-1 py-0.5 text-xs text-right"
                      data-testid={`outstanding-override-${od.drawdown_investor_id}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Transferee panel */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 space-y-3" data-testid="transferee-panel">
          <h2 className="font-semibold text-gray-800 border-b pb-2">Transferee</h2>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Investor Account (KYC-complete only) *</label>
            <SearchableAccountPicker
              value={transfereeAccountId}
              onChange={setTransfereeAccountId}
              accounts={transferees}
              disabled={isReadOnly || !fundId}
              excludeIds={transferorAccountId ? [transferorAccountId] : []}
              placeholder={fundId
                ? '— Search transferee by name or PAN —'
                : 'Pick the transferor & fund first…'}
              // KYC-incomplete accounts are visible but greyed out so the user
              // can see WHO is ineligible (and why) instead of them vanishing.
              // Exception: if the account already holds a folio in this fund,
              // it has already been through the fund's KYC/on-boarding — the
              // backend also treats it as kyc_complete, but we double-check
              // client-side so an older backend build won't break UX.
              disableFn={(a) => !a.kyc_complete && !a.holds_in_fund}
              renderMeta={(a) => (
                <span className="flex items-center gap-1 flex-shrink-0">
                  {!a.kyc_complete && (
                    <span
                      className="text-[10px] px-1.5 py-0.5 rounded bg-red-50 text-red-700 border border-red-200 font-semibold"
                      title="KYC incomplete — complete KYC to allow transfer"
                    >
                      KYC?
                    </span>
                  )}
                  {a.holds_in_fund && (
                    <span
                      className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold"
                      title="Transferee already has a folio in this fund"
                    >
                      Holds folio
                    </span>
                  )}
                </span>
              )}
              testId="transferee-account"
            />
            {transferees.some(t => !t.kyc_complete && t.id !== transferorAccountId) && (
              <p className="text-[11px] text-gray-500 mt-1">
                Non-KYC-complete accounts are shown but disabled (grey). Complete
                their KYC (PAN + address + contact) to make them eligible.
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Fund (inherited)</label>
              <input value={currentFund?.fund_name || ''} disabled className="w-full text-sm border rounded-lg px-3 py-2 bg-gray-50 text-gray-700" data-testid="transferee-fund" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Class (inherited)</label>
              <input value={className} disabled className="w-full text-sm border rounded-lg px-3 py-2 bg-gray-50 text-gray-700" data-testid="transferee-class" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">PPM No. *</label>
            <input value={transfereePpmNo} onChange={e => setTransfereePpmNo(e.target.value)} disabled={isReadOnly} className="w-full text-sm border rounded-lg px-3 py-2 disabled:bg-gray-50" data-testid="transferee-ppm-no" />
          </div>

          {transfereeHoldsInFund ? (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Folio Handling</label>
              <div className="flex gap-2" data-testid="transfer-folio-mode">
                <label className={`flex-1 border rounded-lg px-3 py-2 text-sm cursor-pointer ${transfereeFolioMode === 'merge' ? 'bg-blue-50 border-blue-400 text-blue-800' : ''}`}>
                  <input type="radio" name="folio-mode" checked={transfereeFolioMode === 'merge'} onChange={() => setTransfereeFolioMode('merge')} disabled={isReadOnly} className="mr-2" />
                  Merge into existing folio
                </label>
                <label className={`flex-1 border rounded-lg px-3 py-2 text-sm cursor-pointer ${transfereeFolioMode === 'new' ? 'bg-blue-50 border-blue-400 text-blue-800' : ''}`}>
                  <input type="radio" name="folio-mode" checked={transfereeFolioMode === 'new'} onChange={() => setTransfereeFolioMode('new')} disabled={isReadOnly} className="mr-2" />
                  Create new folio
                </label>
              </div>
            </div>
          ) : (
            <div className="text-xs text-gray-500">Transferee has no folio in this fund — a new folio will be auto-generated.</div>
          )}

          {/* Read-only preview of the transactions that would be copied */}
          {Object.values(selectedTxns).some(s => s?.picked) && (
            <div className="border rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-700 border-b">Transactions that would carry over</div>
              <div className="max-h-40 overflow-y-auto text-xs">
                {Object.entries(selectedTxns).filter(([, v]) => v?.picked).map(([txnId, v]) => (
                  <div key={txnId} className="px-3 py-1.5 border-b last:border-b-0 flex justify-between text-gray-700">
                    <span>{v.date || '—'}</span>
                    <span>₹{fmt(v.amount)} · {fmt(v.units)} u</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Preview snapshot (read-only after save) */}
      {preview && (
        <div className="bg-white rounded-xl border-2 border-blue-200 shadow-sm p-5 space-y-4" data-testid="transfer-preview">
          <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-900">
            <Ban size={16} /> <strong>{preview.banner}</strong>
          </div>
          <h2 className="font-semibold text-gray-800 inline-flex items-center gap-2"><Eye size={16} /> Preview — Before / After</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border rounded-lg p-3 bg-gray-50/50">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Transferor — {preview.transferor.account_name}</h3>
              <table className="w-full text-xs">
                <tbody>
                  <tr><td className="py-0.5 text-gray-500">Fund</td><td className="text-right">{preview.fund_name} · {preview.class_name}</td></tr>
                  <tr><td className="py-0.5 text-gray-500">Commitment</td><td className="text-right"><span className="line-through text-gray-400">{fmt(preview.transferor.line_after.commitment_before)}</span> → <span className="font-semibold text-red-700">{fmt(preview.transferor.line_after.commitment_after)}</span></td></tr>
                  <tr><td className="py-0.5 text-gray-500">Contribution</td><td className="text-right"><span className="line-through text-gray-400">{fmt(preview.transferor.line_after.contribution_before)}</span> → <span className="font-semibold text-red-700">{fmt(preview.transferor.line_after.contribution_after)}</span></td></tr>
                  <tr><td className="py-0.5 text-gray-500">Units</td><td className="text-right"><span className="line-through text-gray-400">{fmt(preview.transferor.line_after.units_before)}</span> → <span className="font-semibold text-red-700">{fmt(preview.transferor.line_after.units_after)}</span></td></tr>
                  <tr><td className="py-0.5 text-gray-500">Status marker</td><td className="text-right">{preview.transferor.line_after.status_marker}</td></tr>
                </tbody>
              </table>
              <div className="mt-3 text-xs text-gray-500">History entry that would be added: <em>{preview.transferor.new_history_transaction.remarks}</em></div>
            </div>

            <div className="border rounded-lg p-3 bg-emerald-50/40">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Transferee — {preview.transferee.account_name}</h3>
              <table className="w-full text-xs">
                <tbody>
                  <tr><td className="py-0.5 text-gray-500">Fund</td><td className="text-right">{preview.fund_name} · {preview.class_name}</td></tr>
                  <tr><td className="py-0.5 text-gray-500">Commitment added</td><td className="text-right font-semibold text-emerald-700">+ {fmt(preview.transferee.line_after.commitment_added)}</td></tr>
                  <tr><td className="py-0.5 text-gray-500">Contribution added</td><td className="text-right font-semibold text-emerald-700">+ {fmt(preview.transferee.line_after.contribution_added)}</td></tr>
                  <tr><td className="py-0.5 text-gray-500">Units added</td><td className="text-right font-semibold text-emerald-700">+ {fmt(preview.transferee.line_after.units_added)}</td></tr>
                  <tr><td className="py-0.5 text-gray-500">PPM</td><td className="text-right">{preview.transferee.line_after.ppm_no || '—'}</td></tr>
                  <tr><td className="py-0.5 text-gray-500">Folio</td><td className="text-right">{preview.transferee.line_after.folio_mode === 'merge' ? 'Merge into existing folio' : 'New folio (auto-generated)'}</td></tr>
                  <tr><td className="py-0.5 text-gray-500">Agreement date</td><td className="text-right">{fmtDate(preview.transferee.line_after.agreement_date)}</td></tr>
                </tbody>
              </table>
              <div className="mt-3 text-xs text-gray-500">History entry that would be added: <em>{preview.transferee.new_history_transaction.remarks}</em></div>
            </div>
          </div>

          {preview.outstanding_drawdown_reassignment?.length > 0 && (
            <div className="border rounded-lg p-3 bg-amber-50/50">
              <h3 className="text-sm font-semibold text-amber-900 mb-2">Outstanding drawdowns that would follow the transferee</h3>
              <table className="w-full text-xs">
                <thead><tr className="text-gray-500"><th className="text-left py-1">Drawdown</th><th className="text-right">Outstanding</th></tr></thead>
                <tbody>
                  {preview.outstanding_drawdown_reassignment.map(od => (
                    <tr key={od.drawdown_investor_id} className="border-t"><td className="py-1">{od.drawdown_number || od.drawdown_id}</td><td className="text-right">₹{fmt(od.outstanding_amount)}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {record?.approval_note && (
            <div className="text-xs text-gray-600"><strong>Checker note:</strong> {record.approval_note}</div>
          )}
        </div>
      )}
    </div>
  );
}
